import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { WhatsappLogo, X, EnvelopeSimple } from "@phosphor-icons/react";

export const FACTSKOOT_WA_NUMBER = "+919844139333";
export const FACTSKOOT_WA_DISPLAY = "+91 98441 39333";
export const FACTSKOOT_EMAIL = "mangocubs.edu@gmail.com";

function waLink(message = "Hi! I'd like to learn more about MangoCubs Preschool OS.") {
  const cleaned = FACTSKOOT_WA_NUMBER.replace(/\D/g, "");
  return `https://wa.me/${cleaned}?text=${encodeURIComponent(message)}`;
}

export default function FloatingWhatsApp() {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");

  return (
    <>
      {/* Bubble */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.4 }}
        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[100]"
        data-testid="floating-whatsapp"
        data-phone={FACTSKOOT_WA_NUMBER}
      >
        <button
          onClick={() => setOpen(true)}
          aria-label={`Chat on WhatsApp ${FACTSKOOT_WA_DISPLAY}`}
          data-phone={FACTSKOOT_WA_NUMBER}
          data-whatsapp-href={waLink()}
          className="group relative h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-[#25D366] text-white shadow-floating flex items-center justify-center hover:scale-105 transition-transform"
          data-testid="floating-whatsapp-btn"
        >
          <WhatsappLogo size={30} weight="fill" />
          <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" />
          <span className="absolute -top-2 -left-2 hidden sm:block whitespace-nowrap rounded-full bg-ink text-white text-xs px-2.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
            Chat with us
          </span>
        </button>
      </motion.div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/30 flex items-end sm:items-end sm:justify-end p-0 sm:p-6"
            onClick={() => setOpen(false)}
            data-testid="whatsapp-sheet"
          >
            <motion.div
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 24, opacity: 0 }}
              className="w-full sm:w-[380px] bg-white rounded-t-3xl sm:rounded-3xl border border-border shadow-floating overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              dir="ltr"
            >
              <div className="bg-[#075E54] text-white px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center">
                    <WhatsappLogo size={22} weight="fill" />
                  </div>
                  <div>
                    <div className="font-heading font-bold">MangoCubs</div>
                    <div className="text-xs opacity-80">Typically replies in a few minutes</div>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center" data-testid="whatsapp-close">
                  <X />
                </button>
              </div>
              <div className="bg-[#ECE5DD] p-5 min-h-[200px]">
                <div className="bg-white rounded-2xl rounded-tl-none p-3 shadow-sm max-w-[85%] text-sm">
                  👋 Hi there! Want to know more about MangoCubs Preschool OS, book a demo, or talk to admissions? Send us a message — we'll reply soon.
                </div>
              </div>
              <div className="p-4 bg-white border-t border-border">
                <textarea
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  placeholder="Type your message…"
                  rows={2}
                  className="w-full px-3 py-2 rounded-xl border border-border bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-[#25D366]"
                  data-testid="whatsapp-input"
                />
                <a
                  href={waLink(msg || undefined)}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 block w-full text-center px-5 py-3 rounded-full bg-[#25D366] text-white font-semibold hover:bg-[#1DA851] transition"
                  data-testid="whatsapp-open"
                >
                  Open WhatsApp · {FACTSKOOT_WA_DISPLAY}
                </a>
                <a
                  href={`mailto:${FACTSKOOT_EMAIL}`}
                  className="mt-2 flex items-center justify-center gap-2 text-sm text-ink-soft hover:text-ink"
                  data-testid="whatsapp-email-link"
                >
                  <EnvelopeSimple weight="duotone" /> Or email {FACTSKOOT_EMAIL}
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function WhatsAppLink({ message, className = "", children, ...rest }) {
  return (
    <a
      href={waLink(message)}
      target="_blank"
      rel="noreferrer"
      className={className}
      {...rest}
    >
      {children}
    </a>
  );
}
